import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState } from "@/components/common/ui";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps, seriesColor } from "@/lib/chart";
import { axisINR } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  AreaChart, Area,
} from "recharts";
import {
  Building2, Landmark, Cpu, Home, ChevronRight, ReceiptText, TrendingUp, Layers, MousePointerClick,
} from "lucide-react";

const slug = (s) => String(s || "").replace(/[^a-zA-Z0-9]+/g, "-");
const pctOf = (v, t) => (t ? Math.round((v / t) * 100) : 0);
const fmtCount = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
// Drop trailing zero-spend months so a trend ends at the last active month
// instead of crashing to zero across the remaining fiscal year.
const activeMonths = (mw = []) => {
  let last = mw.length - 1;
  while (last > 0 && !(mw[last] && mw[last].spent > 0)) last -= 1;
  return mw.slice(0, last + 1);
};

/* ---------- compact horizontal bar list (no recharts, always tidy) ---------- */
function MiniBarList({ items = [], money, colorOffset = 0, max = 8, onItemClick, testId }) {
  const top = items.slice(0, max);
  const maxSpent = top.length ? Math.max(...top.map((r) => r.spent || 0)) : 0;
  const grand = items.reduce((s, r) => s + (r.spent || 0), 0);
  if (top.length === 0) return <EmptyState title="No spend" message="Nothing to show here." />;
  return (
    <div className="space-y-1" data-testid={testId}>
      {top.map((r, i) => {
        const barPct = maxSpent ? Math.max(3, Math.round(((r.spent || 0) / maxSpent) * 100)) : 0;
        const color = seriesColor((i + colorOffset) % 8);
        const clickable = !!onItemClick;
        return (
          <div key={r.name} role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onItemClick(r) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onItemClick(r); } } : undefined}
            className={`group relative flex items-center gap-3 overflow-hidden rounded-md border bg-card px-3 py-2 outline-none transition-colors duration-150 ${clickable ? "cursor-pointer hover:border-[hsl(var(--primary)/0.4)] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary)/0.3)]" : ""}`}
            data-testid={`${testId}-row-${slug(r.name)}`}>
            <span aria-hidden className="absolute inset-y-0 left-0" style={{ width: `${barPct}%`, background: `${color}1f` }} />
            <span className="relative z-10 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
            <span className="relative z-10 min-w-0 flex-1 truncate text-sm font-medium" title={r.name}>{r.name}</span>
            <span className="relative z-10 w-10 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{pctOf(r.spent, grand)}%</span>
            {r.count != null && <span className="relative z-10 hidden w-16 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground sm:inline">{r.count} lines</span>}
            <span className="relative z-10 num w-24 shrink-0 text-right text-sm font-semibold tabular-nums">{money(r.spent)}</span>
            {clickable && <ChevronRight className="relative z-10 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" />}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- KPI chip ---------- */
function Kpi({ label, value, sub, testId }) {
  return (
    <div className="rounded-lg border bg-card p-3" data-testid={testId}>
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="num mt-1 font-heading text-lg font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={typeof sub === "string" ? sub : undefined}>{sub}</p>}
    </div>
  );
}

/* ---------- shared drill dialog: vertical -> platform -> vendor ---------- */
function CorpDrillDialog({ drill, onClose, period }) {
  const { money } = useCurrency();
  const [platform, setPlatform] = useState(null); // null => showing platforms
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPlatform(null); }, [drill]);

  useEffect(() => {
    if (!drill) return;
    let on = true; setLoading(true); setData(null);
    api.corporateDrill(drill.vertical, { entity: drill.entity, platform, period })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData({ items: [], total: 0, level: platform != null ? "vendor" : "platform" }); })
      .finally(() => on && setLoading(false));
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill, platform, period]);

  const level = data?.level || (platform != null ? "vendor" : "platform");

  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="corp-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-[hsl(var(--primary))]" />
            {drill?.vertical} spend {drill?.entity ? `· ${drill.entity}` : "· all corporate"}
          </DialogTitle>
          <DialogDescription>
            {level === "platform" ? "By platform — tap a platform to see its vendors." : "By vendor for the selected platform."}
          </DialogDescription>
        </DialogHeader>

        {/* breadcrumb */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="corp-drill-breadcrumb">
          <button type="button" onClick={() => setPlatform(null)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors duration-150 ${platform == null ? "border-primary bg-[hsl(var(--primary)/0.1)] font-medium text-primary" : "hover:bg-[hsl(var(--surface-2))]"}`}>
            <Home className="h-3 w-3" /> {drill?.vertical}
          </button>
          {platform != null && (
            <>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-[hsl(var(--primary)/0.1)] px-2.5 py-1 text-xs font-medium text-primary">
                <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Platform</span>{platform}
              </span>
            </>
          )}
          <span className="num ml-auto text-xs font-semibold tabular-nums" data-testid="corp-drill-total">{money(data?.total || 0)}</span>
        </div>

        {loading && !data ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-md" />)}</div>
        ) : (data?.items || []).length === 0 ? (
          <EmptyState title="No spend" message="No spend for this selection." />
        ) : (
          <ScrollArea className="max-h-[52vh]">
            <MiniBarList items={data.items} money={money} max={50}
              onItemClick={level === "platform" ? (r) => setPlatform(r.name) : undefined}
              testId="corp-drill-list" />
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- per-entity section shell ---------- */
function EntityShell({ id, icon: Icon, accent, borderClass, title, subtitle, description, kpis, children, hero }) {
  return (
    <div id={id} className="scroll-mt-24">
      <SectionCard
        className={borderClass}
        title={(
          <span className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}26`, color: accent }}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>{subtitle}</span>
              <span className="flex items-center gap-2">{title}{hero && <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium" style={{ borderColor: `${accent}59`, background: `${accent}1a`, color: accent }}>Spotlight</span>}</span>
            </span>
          </span>
        )}
        description={description}
        testId={id}>
        {kpis && <div className="grid grid-cols-2 gap-3 border-b p-4 sm:grid-cols-4">{kpis}</div>}
        {children}
      </SectionCard>
    </div>
  );
}

/* ============================ MAIN ============================ */
export function CorporateSpends({ period = "FY", overview, loading }) {
  const { money } = useCurrency();
  const [entities, setEntities] = useState({});     // name -> data
  const [entLoading, setEntLoading] = useState(true);
  const [drill, setDrill] = useState(null);         // { vertical, entity }

  useEffect(() => {
    let on = true; setEntLoading(true);
    Promise.all(["Sumadhura", "Signet", "Tech"].map((e) => api.corporateEntity(e, period).then((d) => [e, d]).catch(() => [e, null])))
      .then((pairs) => { if (on) setEntities(Object.fromEntries(pairs)); })
      .finally(() => on && setEntLoading(false));
    return () => { on = false; };
  }, [period]);

  const scrollTo = (name) => {
    const key = name === "Technology" ? "Tech" : name;
    const el = document.getElementById(`corp-entity-${slug(key)}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const su = entities.Sumadhura, si = entities.Signet, te = entities.Tech;

  return (
    <>
      {/* ---------- Combined distribution (click a vertical to drill) ---------- */}
      <SectionCard key="mh-verticals" title="Where the Corporate Money Goes"
        description="How corporate spend splits across verticals and entities (Sumadhura / Signet / Tech). Corporate spends only — never blended with project spends."
        action={<Badge variant="outline" className="gap-1 text-[10px]"><MousePointerClick className="h-3 w-3" /> Click a vertical to drill</Badge>}
        testId="card-mh-corporate-dist">
        {(loading && !overview) ? (
          <div className="p-4"><Skeleton className="h-72 w-full rounded-xl" /></div>
        ) : (overview && overview.total > 0) ? (
          <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
            <div data-testid="corp-dist-verticals">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">By corporate vertical</p>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.by_vertical} margin={{ top: 8, right: 12, left: 4, bottom: 0 }} barCategoryGap="24%">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                    <XAxis dataKey="label" {...axisProps} interval={0} angle={-20} textAnchor="end" height={54} />
                    <YAxis tickFormatter={axisINR} {...axisProps} width={48} />
                    <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                    <Bar dataKey="spent" name="Spend" radius={[4, 4, 0, 0]} barSize={26} isAnimationActive={false}
                      cursor="pointer" onClick={(d) => d && d.name && setDrill({ vertical: d.name, entity: null })}>
                      {overview.by_vertical.map((_, i) => <Cell key={i} fill={seriesColor(i)} cursor="pointer" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div data-testid="corp-dist-entities">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">By entity <span className="normal-case text-muted-foreground/70">(click to jump)</span></p>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.by_entity} layout="vertical" margin={{ top: 8, right: 44, left: 8, bottom: 0 }} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                    <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                    <YAxis type="category" dataKey="name" {...axisProps} width={78} />
                    <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                    <Bar dataKey="spent" name="Spend" radius={[0, 4, 4, 0]} barSize={26} isAnimationActive={false}
                      cursor="pointer" onClick={(d) => d && d.name && scrollTo(d.name)}>
                      {overview.by_entity.map((_, i) => <Cell key={i} fill={seriesColor(i + 3)} cursor="pointer" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6"><EmptyState title="No corporate spend" message="No corporate-tagged spend for this period." /></div>
        )}
      </SectionCard>

      {/* ---------- Per-entity sections ---------- */}
      {entLoading && !su ? (
        <SectionCard title="Corporate entities" testId="corp-entities-loading">
          <div className="space-y-3 p-4">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}</div>
        </SectionCard>
      ) : (
        <>
          {/* ===== Sumadhura ===== */}
          {su && su.total > 0 && (
            <EntityShell id="corp-entity-Sumadhura" icon={Building2} accent="hsl(var(--chart-cat-2))"
              borderClass="border-l-[3px] border-l-[hsl(var(--chart-cat-2))]"
              subtitle="Corporate entity" title="Sumadhura — Brand Spend"
              description="Brand-level, non-project spend across all verticals. Click a vertical to drill into platform → vendor."
              kpis={<>
                <Kpi label="Total spend" value={money(su.total)} testId="sumadhura-kpi-total" />
                <Kpi label="Line items" value={fmtCount(su.count)} testId="sumadhura-kpi-count" />
                <Kpi label="Verticals" value={fmtCount(su.by_vertical.length)} testId="sumadhura-kpi-verticals" />
                <Kpi label="Top vendor" value={<span className="truncate text-sm">{su.top_vendor || "—"}</span>} testId="sumadhura-kpi-vendor" />
              </>}>
              <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Layers className="h-3.5 w-3.5" /> By vertical <span className="normal-case text-muted-foreground/70">(click to drill)</span></p>
                  <div style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={su.by_vertical} margin={{ top: 8, right: 12, left: 4, bottom: 0 }} barCategoryGap="22%">
                        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                        <XAxis dataKey="name" {...axisProps} interval={0} angle={-20} textAnchor="end" height={54} />
                        <YAxis tickFormatter={axisINR} {...axisProps} width={48} />
                        <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                        <Bar dataKey="spent" name="Spend" radius={[4, 4, 0, 0]} barSize={24} isAnimationActive={false}
                          cursor="pointer" onClick={(d) => d && d.name && setDrill({ vertical: d.name, entity: "Sumadhura" })}>
                          {su.by_vertical.map((_, i) => <Cell key={i} fill={seriesColor(i + 1)} cursor="pointer" />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><ReceiptText className="h-3.5 w-3.5" /> Activities &amp; spend</p>
                  <ScrollArea className="max-h-[280px]">
                    <table className="w-full border-collapse" data-testid="sumadhura-activities">
                      <thead><tr>
                        {["Activity", "Vertical", "Vendor", "Amount"].map((h, i) => (
                          <th key={h} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${h === "Amount" ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {su.activities.map((r, i) => (
                          <tr key={i} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`}>
                            <td className="td-sticky-left px-3 py-2 text-sm font-medium">{r.activity}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.vertical}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                            <td className="px-3 py-2 num text-right text-sm font-semibold tabular-nums">{money(r.spent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              </div>
            </EntityShell>
          )}

          {/* ===== Signet (spotlight) ===== */}
          {si && si.total > 0 && (
            <EntityShell id="corp-entity-Signet" icon={Landmark} accent="hsl(var(--chart-cat-5))" hero
              borderClass="border-l-[3px] border-l-[hsl(var(--chart-cat-5))]"
              subtitle="Corporate entity" title="Signet — Office Expenses"
              description="Office running costs. Composition and month-over-month trend at a glance."
              kpis={<>
                <Kpi label="Total spend" value={money(si.total)} testId="signet-kpi-total" />
                <Kpi label="Line items" value={fmtCount(si.count)} testId="signet-kpi-count" />
                <Kpi label="Cost heads" value={fmtCount(si.by_activity.length)} testId="signet-kpi-heads" />
                <Kpi label="Top vendor" value={<span className="truncate text-sm">{si.top_vendor || "—"}</span>} testId="signet-kpi-vendor" />
              </>}>
              <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Layers className="h-3.5 w-3.5 text-[hsl(var(--chart-cat-5))]" /> Expense composition</p>
                  <MiniBarList items={si.by_activity} money={money} max={9} colorOffset={4} testId="signet-composition" />
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><TrendingUp className="h-3.5 w-3.5 text-[hsl(var(--chart-cat-5))]" /> Month-over-month trend</p>
                  <div style={{ height: 250 }} data-testid="signet-trend">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={activeMonths(si.monthwise)} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                        <defs>
                          <linearGradient id="signetFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--chart-cat-5))" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="hsl(var(--chart-cat-5))" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                        <XAxis dataKey="month" {...axisProps} interval={0} angle={-30} textAnchor="end" height={50} />
                        <YAxis tickFormatter={axisINR} {...axisProps} width={48} />
                        <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ stroke: "hsl(var(--border))" }} />
                        <Area type="monotone" dataKey="spent" name="Spend" stroke="hsl(var(--chart-cat-5))" strokeWidth={2} fill="url(#signetFill)" isAnimationActive={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="border-t p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><ReceiptText className="h-3.5 w-3.5" /> Top vendors</p>
                <MiniBarList items={si.by_vendor} money={money} max={6} colorOffset={2} testId="signet-vendors" />
              </div>
            </EntityShell>
          )}

          {/* ===== Tech ===== */}
          {te && te.total > 0 && (
            <EntityShell id="corp-entity-Tech" icon={Cpu} accent="hsl(var(--chart-cat-1))"
              borderClass="border-l-[3px] border-l-[hsl(var(--chart-cat-1))]"
              subtitle="Corporate entity" title="Tech — Technology Services"
              description="Tech-services spend incurred by Sumadhura office staff, broken down by service and vendor."
              kpis={<>
                <Kpi label="Total spend" value={money(te.total)} testId="tech-kpi-total" />
                <Kpi label="Line items" value={fmtCount(te.count)} testId="tech-kpi-count" />
                <Kpi label="Services" value={fmtCount(te.by_activity.length)} testId="tech-kpi-services" />
                <Kpi label="Vendors" value={fmtCount(te.by_vendor.length)} testId="tech-kpi-vendors" />
              </>}>
              <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Cpu className="h-3.5 w-3.5" /> By service</p>
                  <MiniBarList items={te.by_activity} money={money} max={9} colorOffset={0} testId="tech-services" />
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><ReceiptText className="h-3.5 w-3.5" /> By vendor</p>
                  <MiniBarList items={te.by_vendor} money={money} max={9} colorOffset={3} testId="tech-vendors" />
                </div>
              </div>
            </EntityShell>
          )}
        </>
      )}

      <CorpDrillDialog drill={drill} onClose={() => setDrill(null)} period={period} />
    </>
  );
}
